import gsap from 'gsap'
import { once } from 'ramda'
import type { PerspectiveCamera } from 'three'
import { Vector3 } from 'three'
import type CameraControls from 'camera-controls'
import type { CameraViewData } from '@/entities/model3d'

/*
Tween a camera in a _straight line_ instead of using camera-controls' built-in
spherical transitions. Useful when doing dolly-to-zoom, etc
 */

const _curTarget = new Vector3()
const _curPosition = new Vector3()

// Returns a function to cancel the tween
const tweenCameraLinear = (
  camera: PerspectiveCamera,
  controls: CameraControls,
  destinationData: Partial<CameraViewData>,
  duration: number = 3,
  onUpdate: () => void = () => {},
  onComplete: () => void = () => {}
): (() => void) => {
  const { fov, position, target } = destinationData
  let fovTween: gsap.core.Tween | null = null
  const completeOnlyOnce = once(onComplete)

  if (fov) {
    fovTween = gsap.to(camera, {
      duration,
      fov: fov.x,
      onUpdate: () => {
        camera.updateProjectionMatrix()
        onUpdate()
      },
    })
  }

  let positionTween: gsap.core.Tween | null = null
  if (position) {
    controls.getTarget(_curTarget, false)
    controls.getPosition(_curPosition, false)
    const { x: px, y: py, z: pz } = _curPosition
    const { x: tx, y: ty, z: tz } = _curTarget
    let coords = { px, py, pz, tx, ty, tz }
    const endingTarget = target || _curTarget
    positionTween = gsap.to(coords, {
      duration,
      px: position.x,
      py: position.y,
      pz: position.z,
      tx: endingTarget.x,
      ty: endingTarget.y,
      tz: endingTarget.z,
      onUpdate() {
        controls.setLookAt(coords.px, coords.py, coords.pz, coords.tx, coords.ty, coords.tz, true)
      },
      onComplete: completeOnlyOnce,
      onInterrupt: completeOnlyOnce,
    })
  }

  return () => {
    fovTween?.kill()
    positionTween?.kill()
    completeOnlyOnce()
  }
}

export { tweenCameraLinear }
